import { fetchCurrentUser } from './user.duck';
import { usersAPI } from '../api';
import {
  getSelectedOrganizationProfile,
  getUserOrganizationProfiles,
} from '../util/organizations';
import {
  getSelectedOrganizationWithPermissions,
  canAccessDashboardPage,
} from '../util/dashboardPermissions';
import { storableError } from '../util/errors';
import { getUserMemberships } from '../util/orgMembership';

// ================ Action types ================ //

export const FETCH_ORGANIZATION_PROFILES_REQUEST = 'app/assets/FETCH_ORGANIZATION_PROFILES_REQUEST';
export const FETCH_ORGANIZATION_PROFILES_SUCCESS = 'app/assets/FETCH_ORGANIZATION_PROFILES_SUCCESS';
export const FETCH_ORGANIZATION_PROFILES_ERROR = 'app/assets/FETCH_ORGANIZATION_PROFILES_ERROR';

// DEPRECATED: These actions are no longer used for context switching.
// Context is now determined by URL path (e.g., /dashboard/org/:orgId/*)
// Keeping for backward compatibility but may be removed in future.
export const SELECT_ORGANIZATION_PROFILE_REQUEST = 'app/assets/SELECT_ORGANIZATION_PROFILE_REQUEST';
export const SELECT_ORGANIZATION_PROFILE_SUCCESS = 'app/assets/SELECT_ORGANIZATION_PROFILE_SUCCESS';
export const SELECT_ORGANIZATION_PROFILE_ERROR = 'app/assets/SELECT_ORGANIZATION_PROFILE_ERROR';

// ================ Reducer ================ //

const initialState = {
  fetchOrganizationProfilesInProgress: false,
  fetchOrganizationProfilesError: null,
  organizationProfiles: [],
  selectOrganizationProfileInProgress: false,
  selectOrganizationProfileError: null,
};

export default function assetReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_ORGANIZATION_PROFILES_REQUEST:
      return {
        ...state,
        fetchOrganizationProfilesInProgress: true,
        fetchOrganizationProfilesError: null,
      };
    case FETCH_ORGANIZATION_PROFILES_SUCCESS:
      return {
        ...state,
        fetchOrganizationProfilesInProgress: false,
        fetchOrganizationProfilesError: null,
        organizationProfiles: payload.organizationProfiles,
      };
    case FETCH_ORGANIZATION_PROFILES_ERROR:
      return {
        ...state,
        fetchOrganizationProfilesInProgress: false,
        fetchOrganizationProfilesError: payload,
      };

    case SELECT_ORGANIZATION_PROFILE_REQUEST:
      return {
        ...state,
        selectOrganizationProfileInProgress: true,
        selectOrganizationProfileError: null,
      };
    case SELECT_ORGANIZATION_PROFILE_SUCCESS:
      return {
        ...state,
        selectOrganizationProfileInProgress: false,
        selectOrganizationProfileError: null,
      };
    case SELECT_ORGANIZATION_PROFILE_ERROR:
      return {
        ...state,
        selectOrganizationProfileInProgress: false,
        selectOrganizationProfileError: payload,
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const fetchOrganizationProfilesRequest = () => ({
  type: FETCH_ORGANIZATION_PROFILES_REQUEST,
});
export const fetchOrganizationProfilesSuccess = organizationProfiles => ({
  type: FETCH_ORGANIZATION_PROFILES_SUCCESS,
  payload: { organizationProfiles },
});
export const fetchOrganizationProfilesError = error => ({
  type: FETCH_ORGANIZATION_PROFILES_ERROR,
  payload: error,
});

export const selectOrganizationProfileRequest = () => ({
  type: SELECT_ORGANIZATION_PROFILE_REQUEST,
});
export const selectOrganizationProfileSuccess = () => ({
  type: SELECT_ORGANIZATION_PROFILE_SUCCESS,
});
export const selectOrganizationProfileError = error => ({
  type: SELECT_ORGANIZATION_PROFILE_ERROR,
  payload: error,
});

// ================ Thunks ================ //

/**
 * Normalize the user's `privateData.memberOfOrganizations[]` entries into the
 * same shape the dashboard expects from organizationProfiles —
 * { id, name, profileImage, role, creatorId, invited } — so the switcher,
 * DashboardWrapper, etc. don't need to special-case.
 *
 * Spec: UPDATE.md §3.6.7
 */
const fetchMemberOfOrganizationProfiles = currentUser => {
  const memberships = getUserMemberships(currentUser);
  return memberships.map(m => ({
    id: m.orgId,
    name: m.orgName,
    profileImage: m.orgLogo,
    role: m.role,
    creatorId: m.ownerId, // matches legacy `creatorId` shape used by some UI
    invited: true,
    joinedAt: m.joinedAt,
  }));
};

const orgLogoFromOwner = (owner, orgId) => {
  const profiles = owner?.attributes?.profile?.publicData?.organizationProfiles || [];
  const op = profiles.find(o => o?.id === orgId);
  return op?.profileImage || op?.logo || null;
};

const ownerIdOf = profile => profile?.ownerId || profile?.creatorId || null;

/**
 * Member-org logos are stored as a snapshot (`orgLogo`) captured when the
 * member was added, so the switcher shows a stale/empty logo if the org
 * set or changed its logo afterwards. Org branding is public, so resolve
 * member-org logos *live* from the owner's public profile on every load
 * — this both fixes a missing logo and propagates logo changes. Owned
 * orgs are skipped (the owner is the current user, already fresh). Falls
 * back to the existing snapshot if the live lookup yields nothing.
 * Bounded by the number of distinct owners.
 */
const enrichMemberLogos = async (profiles, sdk) => {
  const members = (profiles || []).filter(p => p && !p.isOwner && ownerIdOf(p));
  if (members.length === 0) return profiles;

  const ownerIds = [...new Set(members.map(ownerIdOf))];
  const ownerById = {};
  await Promise.all(
    ownerIds.map(async id => {
      try {
        const res = await sdk.users.show({ id });
        ownerById[id] = res?.data?.data || null;
      } catch (e) {
        ownerById[id] = null;
      }
    })
  );

  return profiles.map(p => {
    if (!p || p.isOwner || !ownerIdOf(p)) return p;
    const liveLogo = orgLogoFromOwner(ownerById[ownerIdOf(p)], p.id);
    return liveLogo ? { ...p, profileImage: liveLogo } : p;
  });
};

// Queries organizations and their profiles.
export const fetchOrganizationProfiles = () => async (dispatch, getState, sdk) => {
  dispatch(fetchOrganizationProfilesRequest());

  // A visitor who is not signed in belongs to no organization, and the
  // currentUser call would fail with a 403 that is still billed.
  if (!getState().Auth?.isAuthenticated) {
    dispatch(fetchOrganizationProfilesSuccess([]));
    return [];
  }

  try {
    // The signed-in user is usually loaded already; read it only when it is not.
    const currentUser =
      getState().user?.currentUser ||
      (await sdk.currentUser.show().then(response => response.data.data));

    const memberOfOrgProfiles = fetchMemberOfOrganizationProfiles(currentUser);

    // Enrich the *merged* list — member orgs surfaced by getAllUserOrgs
    // (with their stale snapshot logo) would otherwise dedupe out any
    // enrichment applied only to the invited-profiles input.
    const userOrganizationProfiles = await enrichMemberLogos(
      getUserOrganizationProfiles(currentUser, memberOfOrgProfiles),
      sdk
    );

    dispatch(fetchOrganizationProfilesSuccess(userOrganizationProfiles));
    return userOrganizationProfiles;
  } catch (error) {
    dispatch(fetchOrganizationProfilesError(storableError(error)));
  }
};

/**
 * Fetch organization by ID with role-based permissions.
 *
 * With URL-based routing, the orgId comes from the URL path (e.g., /dashboard/org/:orgId/*).
 * This function can be used to get organization data when you have the orgId from the URL.
 *
 * @param {string} orgId - The organization ID from URL params
 * @param {object} currentUser - Optional current user (will be fetched if not provided)
 * @returns {object|null} Organization with roles, permissions, and subscription info
 */
export const fetchOrganizationById = (orgId, currentUser) => async (dispatch, getState, sdk) => {
  try {
    // Ensure we have organization profiles loaded
    const state = getState();
    let organizationProfiles = state.organizations?.organizationProfiles;

    if (!organizationProfiles || organizationProfiles.length === 0) {
      organizationProfiles = await dispatch(fetchOrganizationProfiles());
    }

    // Find the organization by ID
    const organization = getSelectedOrganizationProfile(organizationProfiles, orgId);

    if (!organization) {
      return null;
    }

    // Get current user for permission checks
    let cu = currentUser;
    if (!cu) {
      const currentUserResponse = await sdk.currentUser.show();
      cu = currentUserResponse.data.data;
    }

    // If we have the user, we can add permission info
    if (cu) {
      const selectedOrgWithPermissions = getSelectedOrganizationWithPermissions(cu, orgId);
      if (selectedOrgWithPermissions) {
        return selectedOrgWithPermissions;
      }
    }

    return organization;
  } catch (error) {
    console.error('Error fetching organization by ID:', error);
    return null;
  }
};

/**
 * Check if current user can access a specific dashboard page
 */
export const checkDashboardAccess = (pageName, context = {}) => async (dispatch, getState, sdk) => {
  try {
    // Always get fresh user data from API to ensure we have latest info
    const currentUserResponse = await sdk.currentUser.show();
    const currentUser = currentUserResponse.data.data;
    
    if (!currentUser) {
      return { canAccess: false, reason: 'Not authenticated' };
    }

    const selectedOrg = getSelectedOrganizationWithPermissions(currentUser);
    
    if (!selectedOrg) {
      return { canAccess: false, reason: 'No accessible organization' };
    }

    return canAccessDashboardPage(currentUser, selectedOrg.id, pageName, context);
    
  } catch (error) {
    console.error('Error checking dashboard access:', error);
    return { canAccess: false, reason: 'Error checking permissions' };
  }
};

/**
 * DEPRECATED: This thunk is no longer used for context switching.
 *
 * With URL-based routing, context is determined by the URL path:
 * - /dashboard/creator/* = Creator context
 * - /dashboard/org/:orgId/* = Organization context
 *
 * Switching contexts is now done via native navigation (NamedLink),
 * not by updating the user's profile. This eliminates 409 conflicts
 * and enables bookmarkable/shareable dashboard URLs.
 *
 * Keeping this function for backward compatibility but it should not
 * be called for context switching. May be removed in future cleanup.
 */
export const selectOrganizationProfile = organizationProfileId => async (dispatch, getState, sdk) => {
  console.warn(
    'selectOrganizationProfile is deprecated. Context switching now uses URL-based routing.'
  );

  dispatch(selectOrganizationProfileRequest());

  try {
    // Step 1: Update publicData for legacy compatibility
    const response = await sdk.currentUser.updateProfile({
      publicData: {
        selectedOrganizationProfileId: organizationProfileId,
      },
    });

    // Step 2: Update metadata for new permission system (required for permissions to work)
    // Wrap in try-catch with retry logic for 409 conflicts
    const updateMetadata = async (retryCount = 0) => {
      try {
        // Get fresh user data from API to ensure we have latest metadata
        const currentUserResponse = await sdk.currentUser.show();
        const currentUser = currentUserResponse.data.data;
        const currentMetadata = currentUser.attributes?.profile?.metadata || {};

        const updatedMetadata = {
          ...currentMetadata,
          selectedOrganizationId: organizationProfileId,
          lastSelectedAt: new Date().toISOString(),
        };

        // Use server endpoint for metadata updates
        await usersAPI.updateProfile({
          id: currentUser.id.uuid,
          metadata: updatedMetadata,
        });
      } catch (metadataError) {
        // Handle 409 Conflict by retrying with fresh data
        if (metadataError?.response?.status === 409 && retryCount < 2) {
          // Wait a bit before retry
          await new Promise(resolve => setTimeout(resolve, 100 * (retryCount + 1)));
          return updateMetadata(retryCount + 1);
        }
        // Log but don't fail the whole operation - publicData update succeeded
        console.warn('Failed to update metadata after retries:', metadataError);
      }
    };

    await updateMetadata();

    dispatch(selectOrganizationProfileSuccess(response));
    dispatch(fetchCurrentUser());

    return response;
  } catch (error) {
    dispatch(selectOrganizationProfileError(storableError(error)));
    throw error;
  }
};
